/**
 * Profitability (admin only; App.js wraps it in <AdminRoute> and every /api/profitability call
 * requires the admin role on the server).
 *
 *   Report tab    revenue (incl GST) -> net revenue (excl GST) -> cost of goods -> fees -> contribution
 *                 -> ad spend -> profit after ads (ad columns appear once an ads connection has synced),
 *                 by platform / brand / category / SKU, monthly trend, loss-makers, CSV export.
 *   Settings tab  GST % per category (default 18%) and fee rules per platform + category
 *                 (commission % of selling price, fixed fee per unit, shipping per unit).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AlertTriangle, Download, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../contexts/CountryContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { KpiCard } from '../components/sales/KpiCard';
import { downloadCsv, indiaToday, shiftDay, shortMoney, wholeMoney } from '../components/sales/format';
import { LoadError, errorMessage } from '../components/LoadError';
import { platformLabel } from '../config/platforms';
import { useChannels } from '../hooks/useChannels';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/profitability`;
const ALL = 'ALL';
const ALL_CATEGORIES = '*';

const platformName = code => platformLabel(code);
const pct = value => (value == null ? '—' : `${value.toFixed(1)}%`);
const tone = value => (value < 0 ? 'text-red-600' : 'text-gray-900');

const PERIODS = [
  { id: '30', label: 'Last 30 days' },
  { id: 'mtd', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: '90', label: 'Last 90 days' },
  { id: 'ytd', label: 'This year' },
];

const periodRange = id => {
  const end = shiftDay(indiaToday(), -1);
  if (id === 'mtd') return { date_from: `${end.slice(0, 7)}-01`, date_to: end };
  if (id === 'ytd') return { date_from: `${end.slice(0, 4)}-01-01`, date_to: end };
  if (id === 'last_month') {
    const lastDay = shiftDay(`${indiaToday().slice(0, 7)}-01`, -1);
    return { date_from: `${lastDay.slice(0, 7)}-01`, date_to: lastDay };
  }
  return { date_from: shiftDay(end, 1 - Number(id)), date_to: end };
};

const BREAKDOWNS = {
  platform: { label: 'Platform', key: 'by_platform', name: row => row.label || platformName(row.platform) },
  brand: { label: 'Brand', key: 'by_brand', name: row => row.brand },
  category: { label: 'Category', key: 'by_category', name: row => row.category },
  sku: { label: 'SKU', key: 'by_sku', name: row => `${row.sku_code} · ${row.product_name}` },
};

const Select = ({ value, onChange, children, testId, label }) => (
  <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
    {label}
    <select
      value={value}
      onChange={event => onChange(event.target.value)}
      data-testid={testId}
      className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900"
    >
      {children}
    </select>
  </label>
);

// ---------------------------------------------------------------------------- report

const ProfitTable = ({ rows, name, testId, ads = false }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
    <table className="w-full text-sm" data-testid={testId}>
      <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-600">
        <tr>
          <th className="px-3 py-2">Name</th>
          <th className="px-3 py-2 text-right">Units</th>
          <th className="px-3 py-2 text-right">Revenue (incl GST)</th>
          <th className="px-3 py-2 text-right">Net revenue</th>
          <th className="px-3 py-2 text-right">Cost of goods</th>
          <th className="px-3 py-2 text-right">Gross profit</th>
          <th className="px-3 py-2 text-right">Gross margin</th>
          <th className="px-3 py-2 text-right">Fees</th>
          <th className="px-3 py-2 text-right">Contribution</th>
          <th className="px-3 py-2 text-right">Contribution margin</th>
          {ads && <th className="px-3 py-2 text-right">Ad spend</th>}
          {ads && <th className="px-3 py-2 text-right">Profit after ads</th>}
          {ads && <th className="px-3 py-2 text-right">Net margin</th>}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.length === 0 && (
          <tr><td colSpan={ads ? 13 : 10} className="px-3 py-6 text-center text-gray-500">No sales in this period.</td></tr>
        )}
        {rows.map(row => (
          <tr key={name(row)}>
            <td className="px-3 py-2 font-medium text-gray-900">
              {name(row)}
              {row.uncosted_units > 0 && (
                <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800" title="Some units have no cost price, so profit is overstated">
                  {row.uncosted_units} units without cost
                </span>
              )}
            </td>
            <td className="px-3 py-2 text-right">{row.units.toLocaleString('en-IN')}</td>
            <td className="px-3 py-2 text-right">{wholeMoney(row.revenue)}</td>
            <td className="px-3 py-2 text-right">{wholeMoney(row.net_revenue)}</td>
            <td className="px-3 py-2 text-right">{wholeMoney(row.cogs)}</td>
            <td className={`px-3 py-2 text-right ${tone(row.gross_profit)}`}>{wholeMoney(row.gross_profit)}</td>
            <td className={`px-3 py-2 text-right ${tone(row.gross_margin_pct)}`}>{pct(row.gross_margin_pct)}</td>
            <td className="px-3 py-2 text-right">{wholeMoney(row.fees)}</td>
            <td className={`px-3 py-2 text-right font-semibold ${tone(row.contribution)}`}>{wholeMoney(row.contribution)}</td>
            <td className={`px-3 py-2 text-right ${tone(row.contribution_margin_pct)}`}>{pct(row.contribution_margin_pct)}</td>
            {ads && <td className="px-3 py-2 text-right">{wholeMoney(row.ad_spend)}</td>}
            {ads && <td className={`px-3 py-2 text-right font-semibold ${tone(row.net_profit)}`}>{wholeMoney(row.net_profit)}</td>}
            {ads && <td className={`px-3 py-2 text-right ${tone(row.net_margin_pct)}`}>{pct(row.net_margin_pct)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const exportRows = (rows, name) => [
  ['Name', 'Units', 'Revenue (incl GST)', 'Net revenue (excl GST)', 'Cost of goods', 'Gross profit', 'Gross margin %',
    'Fees', 'Contribution', 'Contribution margin %', 'Ad spend', 'Profit after ads', 'Net margin %', 'Units without cost'],
  ...rows.map(row => [name(row), row.units, row.revenue, row.net_revenue, row.cogs, row.gross_profit, row.gross_margin_pct,
    row.fees, row.contribution, row.contribution_margin_pct, row.ad_spend, row.net_profit, row.net_margin_pct, row.uncosted_units]),
];

const ProfitReport = ({ country }) => {
  const [period, setPeriod] = useState('30');
  const [filters, setFilters] = useState({ platform: ALL, brand: ALL, category: ALL });
  const [breakdown, setBreakdown] = useState('platform');
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const [version, setVersion] = useState(0);

  const query = useMemo(() => {
    const params = new URLSearchParams({ country, ...periodRange(period) });
    Object.entries(filters).forEach(([key, value]) => { if (value !== ALL) params.set(key, value); });
    return params.toString();
  }, [country, period, filters]);

  useEffect(() => {
    const controller = new AbortController();
    setState(prev => ({ ...prev, loading: true, error: null }));
    axios.get(`${API}/report?${query}`, { signal: controller.signal })
      .then(response => setState({ data: response.data, loading: false, error: null }))
      .catch(error => { if (!controller.signal.aborted) setState(prev => ({ ...prev, loading: false, error })); });
    return () => controller.abort();
  }, [query, version]);

  const { data, loading, error } = state;
  const s = data?.summary;
  const p = data?.previous;
  const view = BREAKDOWNS[breakdown];
  const rows = data?.[view.key] || [];
  const ads = !!data?.meta.has_ad_spend;
  const setFilter = key => value => setFilters(prev => ({ ...prev, [key]: value }));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-3">
        <Select label="Period" value={period} onChange={setPeriod} testId="profit-period">
          {PERIODS.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
        </Select>
        <Select label="Platform" value={filters.platform} onChange={setFilter('platform')} testId="profit-platform">
          <option value={ALL}>All platforms</option>
          {(data?.options.platforms || []).map(code => <option key={code} value={code}>{platformName(code)}</option>)}
        </Select>
        <Select label="Brand" value={filters.brand} onChange={setFilter('brand')} testId="profit-brand">
          <option value={ALL}>All brands</option>
          {(data?.options.brands || []).map(brand => <option key={brand} value={brand}>{brand}</option>)}
        </Select>
        <Select label="Category" value={filters.category} onChange={setFilter('category')} testId="profit-category">
          <option value={ALL}>All categories</option>
          {(data?.options.categories || []).map(cat => <option key={cat} value={cat}>{cat}</option>)}
        </Select>
        <Button variant="outline" onClick={() => setVersion(v => v + 1)} disabled={loading} data-testid="profit-refresh">
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {error && <LoadError what="the profitability report" error={error} onRetry={() => setVersion(v => v + 1)} />}

      {data && (
        <>
          {data.meta.fee_rules === 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900" data-testid="profit-no-fees">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              No marketplace fees are set yet, so contribution equals gross profit. Add fees in the Fee &amp; GST settings tab.
            </div>
          )}
          {data.meta.has_returns && (
            <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800" data-testid="profit-returns">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
              <span>
                Revenue is after returns: {wholeMoney(s.returns_revenue)} of Amazon Seller API sales came back (gross {wholeMoney(s.gross_revenue)}).
                Cost of goods is reversed only for units that went back to sellable stock. Marketplace fees refunded on returns are not deducted yet.
              </span>
            </div>
          )}
          {data.skus_without_gst?.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900" data-testid="profit-gst-fallback">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {data.skus_without_gst.length} SKU{data.skus_without_gst.length === 1 ? '' : 's'} sold in this period
                {data.skus_without_gst.length === 1 ? ' uses' : ' use'} a category or default GST rate. For exact profit, set GST % in SKU Master
                or use &quot;Fill SKU GST from purchases&quot; in Fee &amp; GST settings.
              </span>
            </div>
          )}
          {data.uncosted_skus.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="profit-uncosted">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {data.uncosted_skus.length === 1
                  ? '1 SKU sold in this period has no cost price, so profit is overstated for it.'
                  : `${data.uncosted_skus.length} SKUs sold in this period have no cost price, so profit is overstated for them.`} Add their cost in SKU Master or post a purchase invoice:{' '}
                <span className="font-mono text-xs">{data.uncosted_skus.slice(0, 12).join(', ')}{data.uncosted_skus.length > 12 ? ' …' : ''}</span>
              </span>
            </div>
          )}

          <div className={`grid gap-3 sm:grid-cols-2 ${ads ? 'lg:grid-cols-4' : 'xl:grid-cols-5'} ${loading ? 'opacity-60' : ''}`}>
            <KpiCard label="Revenue (incl GST)" value={shortMoney(s.revenue)} current={s.revenue} previous={p.revenue} compareLabel="vs previous period" testId="profit-kpi-revenue" />
            <KpiCard label="Net revenue (excl GST)" value={shortMoney(s.net_revenue)} current={s.net_revenue} previous={p.net_revenue} compareLabel="vs previous period" testId="profit-kpi-net" />
            <KpiCard label="Gross profit" value={shortMoney(s.gross_profit)} current={s.gross_profit} previous={p.gross_profit} compareLabel="vs previous period" footnote={`Margin ${pct(s.gross_margin_pct)}`} testId="profit-kpi-gross" />
            <KpiCard label="Marketplace fees" value={shortMoney(s.fees)} current={s.fees} previous={p.fees} invert compareLabel="vs previous period" testId="profit-kpi-fees" />
            <KpiCard label="Contribution" value={shortMoney(s.contribution)} current={s.contribution} previous={p.contribution} compareLabel="vs previous period" footnote={`Margin ${pct(s.contribution_margin_pct)}`} testId="profit-kpi-contribution" />
            {ads && (
              <>
                <KpiCard label="Ad spend (excl GST)" value={shortMoney(s.ad_spend)} current={s.ad_spend} previous={p.ad_spend} invert noun="ad spend" compareLabel="vs previous period" testId="profit-kpi-ads" />
                <KpiCard label="Profit after ads" value={shortMoney(s.net_profit)} current={s.net_profit} previous={p.net_profit} compareLabel="vs previous period" footnote={`Margin ${pct(s.net_margin_pct)}`} testId="profit-kpi-net-profit" />
              </>
            )}
          </div>

          {data.trend.length > 1 && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h2 className="mb-3 text-sm font-semibold text-gray-900">Monthly net revenue and {ads ? 'profit' : 'contribution'}</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.trend}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={shortMoney} tick={{ fontSize: 12 }} width={70} />
                    <Tooltip formatter={value => wholeMoney(value)} />
                    <Legend />
                    <Bar dataKey="net_revenue" name="Net revenue" fill="#93C5FD" />
                    <Bar dataKey="contribution" name="Contribution" fill="#16A34A" />
                    {ads && <Bar dataKey="net_profit" name="Profit after ads" fill="#7C3AED" />}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap gap-1" role="tablist">
                {Object.entries(BREAKDOWNS).map(([id, item]) => (
                  <Button key={id} size="sm" variant={breakdown === id ? 'default' : 'outline'} onClick={() => setBreakdown(id)} data-testid={`profit-by-${id}`}>
                    By {item.label.toLowerCase()}
                  </Button>
                ))}
              </div>
              <Button size="sm" variant="outline" disabled={!rows.length} data-testid="profit-export"
                onClick={() => downloadCsv(exportRows(rows, view.name), `profitability-by-${breakdown}-${data.meta.date_from}-to-${data.meta.date_to}.csv`)}>
                <Download className="mr-2 h-4 w-4" /> Export CSV
              </Button>
            </div>
            <ProfitTable rows={rows} name={view.name} ads={ads} testId="profit-breakdown-table" />
          </div>

          {data.loss_makers.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-sm font-semibold text-red-700">Loss-making SKUs ({data.loss_makers.length})</h2>
              <p className="text-xs text-gray-600">
                {ads ? 'Profit after ads is below zero: selling price after GST does not cover purchase cost, fees and ad spend.'
                  : 'Contribution is below zero: selling price after GST does not cover purchase cost plus fees.'}
              </p>
              <ProfitTable rows={data.loss_makers} name={BREAKDOWNS.sku.name} ads={ads} testId="profit-loss-table" />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------- settings

const numberField = value => (value === '' ? '' : Number(value));

const numberOrNull = value => (value === '' || value == null ? null : Number(value));

// Band editor: rows of { <limitKey>: upper limit (blank = no limit), ...value fields }.
const BandEditor = ({ title, help, bands, onChange, limitKey, limitLabel, fields, testId }) => {
  const set = (index, patch) => onChange(bands.map((band, i) => (i === index ? { ...band, ...patch } : band)));
  const blank = Object.fromEntries([[limitKey, ''], ...fields.map(([key]) => [key, 0])]);
  return (
    <div className="space-y-2" data-testid={testId}>
      <div>
        <p className="text-xs font-semibold text-gray-800">{title}</p>
        <p className="text-[11px] text-gray-500">{help}</p>
      </div>
      {bands.map((band, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index} className="flex flex-wrap items-end gap-2">
          <label className="text-[11px] text-gray-600">{limitLabel}
            <Input type="number" min="0" step="0.01" className="h-8 w-28" placeholder="no limit" value={band[limitKey] ?? ''}
              onChange={event => set(index, { [limitKey]: event.target.value })} />
          </label>
          {fields.map(([key, label]) => (
            <label key={key} className="text-[11px] text-gray-600">{label}
              <Input type="number" min="0" step="0.01" className="h-8 w-24" value={band[key]}
                onChange={event => set(index, { [key]: numberField(event.target.value) })} />
            </label>
          ))}
          <Button variant="ghost" size="sm" onClick={() => onChange(bands.filter((_, i) => i !== index))} aria-label="Remove band">
            <Trash2 className="h-4 w-4 text-red-600" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => onChange([...bands, blank])}>
        <Plus className="mr-1 h-3 w-3" /> Add band
      </Button>
    </div>
  );
};

const GstSuggestionsDialog = ({ open, onOpenChange, country, onApplied }) => {
  const [rows, setRows] = useState(null);
  const [chosen, setChosen] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows(null);
    axios.get(`${API}/gst-suggestions?country=${country}`)
      .then(response => { setRows(response.data); setChosen(Object.fromEntries(response.data.map(r => [r.sku_code, true]))); })
      .catch(err => { toast.error(errorMessage(err, 'Could not load suggestions')); setRows([]); });
  }, [open, country]);

  const apply = async () => {
    const items = (rows || []).filter(r => chosen[r.sku_code]).map(r => ({ sku_code: r.sku_code, gst_pct: r.gst_pct }));
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/gst-suggestions/apply?country=${country}`, { items });
      toast.success(data.message);
      onOpenChange(false);
      onApplied();
    } catch (err) {
      toast.error(errorMessage(err, 'Could not apply GST rates'));
    } finally {
      setBusy(false);
    }
  };

  const picked = (rows || []).filter(r => chosen[r.sku_code]).length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="gst-suggestions-dialog">
        <DialogHeader>
          <DialogTitle>Fill GST % from purchase invoices</DialogTitle>
          <DialogDescription>
            SKUs without their own GST %, with the rate on their latest posted purchase invoice. Purchase GST is usually the
            same as the selling GST for traded goods, but check before applying.
          </DialogDescription>
        </DialogHeader>
        {!rows && <p className="text-sm text-gray-500">Loading…</p>}
        {rows && rows.length === 0 && <p className="text-sm text-gray-600">Nothing to fill: every SKU with a posted purchase already has a GST %.</p>}
        {rows && rows.length > 0 && (
          <div className="max-h-[50vh] overflow-y-auto rounded border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
                <tr><th className="p-2" /><th className="p-2">SKU</th><th className="p-2">Category</th><th className="p-2 text-right">GST %</th><th className="p-2">From invoice</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(r => (
                  <tr key={r.sku_code}>
                    <td className="p-2"><input type="checkbox" checked={!!chosen[r.sku_code]} aria-label={`Use ${r.gst_pct}% for ${r.sku_code}`}
                      onChange={event => setChosen(prev => ({ ...prev, [r.sku_code]: event.target.checked }))} /></td>
                    <td className="p-2"><span className="font-mono text-xs">{r.sku_code}</span> <span className="text-gray-600">{r.product_name}</span></td>
                    <td className="p-2 text-gray-600">{r.category}</td>
                    <td className="p-2 text-right font-semibold">{r.gst_pct}%</td>
                    <td className="p-2 text-xs text-gray-600">{r.invoice_no} · {r.invoice_date} · {r.supplier}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={apply} disabled={busy || !picked} className="bg-[#007AFF] hover:bg-[#0062cc] text-white" data-testid="gst-suggestions-apply">
            {busy ? 'Applying…' : `Set GST on ${picked} SKU${picked === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ProfitSettings = ({ country }) => {
  const channelMaster = useChannels();
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openBands, setOpenBands] = useState({});
  const [gstDialog, setGstDialog] = useState(false);

  const load = useCallback(() => {
    setError(null);
    Promise.all([
      axios.get(`${API}/settings?country=${country}`),
      axios.get(`${API}/report?country=${country}`),
    ]).then(([settingsRes, reportRes]) => {
      setSettings(settingsRes.data);
      setCategories(reportRes.data.options.categories);
    }).catch(setError);
  }, [country]);
  useEffect(load, [load]);

  if (error) return <LoadError what="profitability settings" error={error} onRetry={load} />;
  if (!settings) return <div className="text-sm text-gray-500">Loading settings…</div>;

  const update = patch => setSettings(prev => ({ ...prev, ...patch }));
  const setFee = (index, patch) => update({ fees: settings.fees.map((fee, i) => (i === index ? { ...fee, ...patch } : fee)) });
  const addFee = () => update({ fees: [...settings.fees, { platform: 'AMAZON_SELLER', category: ALL_CATEGORIES, commission_pct: 0, fixed_fee: 0,
    shipping_per_unit: 0, payment_pct: 0, price_bands: [], weight_bands: [] }] });
  const removeFee = index => update({ fees: settings.fees.filter((_, i) => i !== index) });
  const setGst = (category, value) => {
    const next = { ...settings.category_gst };
    if (value === '') delete next[category]; else next[category] = Number(value);
    update({ category_gst: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        default_gst_pct: Number(settings.default_gst_pct) || 0,
        category_gst: settings.category_gst,
        fee_gst_pct: Number(settings.fee_gst_pct ?? 18) || 0,
        fee_gst_claimable: settings.fee_gst_claimable !== false,
        fees: settings.fees.map(fee => ({
          platform: fee.platform, category: fee.category,
          commission_pct: Number(fee.commission_pct) || 0, fixed_fee: Number(fee.fixed_fee) || 0,
          shipping_per_unit: Number(fee.shipping_per_unit) || 0, payment_pct: Number(fee.payment_pct) || 0,
          price_bands: (fee.price_bands || []).map(b => ({ up_to: numberOrNull(b.up_to), commission_pct: Number(b.commission_pct) || 0, fixed_fee: Number(b.fixed_fee) || 0 })),
          weight_bands: (fee.weight_bands || []).map(b => ({ up_to_kg: numberOrNull(b.up_to_kg), fee: Number(b.fee) || 0 })),
        })),
      };
      const response = await axios.put(`${API}/settings?country=${country}`, body);
      setSettings(response.data);
      toast.success('Profitability settings saved');
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">GST on selling prices</h2>
            <p className="text-xs text-gray-600">
              Selling prices include GST. Each SKU uses its own <b>GST %</b> from SKU Master first, then its category&apos;s rate below,
              then the default. Leave a category blank to use the default.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setGstDialog(true)} data-testid="profit-fill-gst">
            Fill SKU GST from purchases
          </Button>
        </div>
        <label className="flex items-center gap-3 text-sm">
          <span className="w-48 font-medium text-gray-700">Default GST %</span>
          <Input type="number" min="0" max="100" step="0.01" className="w-28" value={settings.default_gst_pct}
            onChange={event => update({ default_gst_pct: numberField(event.target.value) })} data-testid="profit-default-gst" />
        </label>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map(category => (
            <label key={category} className="flex items-center gap-3 text-sm">
              <span className="w-48 truncate text-gray-700" title={category}>{category}</span>
              <Input type="number" min="0" max="100" step="0.01" className="w-28" placeholder={`${settings.default_gst_pct}`}
                value={settings.category_gst[category] ?? ''} onChange={event => setGst(category, event.target.value)} />
            </label>
          ))}
        </div>
      </section>

      <section className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Marketplace fees</h2>
          <p className="text-xs text-gray-600">
            Commission and payment % are on the selling price (incl GST). A rule for a specific category overrides that platform&apos;s
            &quot;All categories&quot; rule. Use <b>Bands</b> when the fee depends on the selling price or the product weight.
            Platforms with no rule are treated as zero fees.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="profit-fee-table">
            <thead className="text-left text-xs uppercase tracking-wide text-gray-600">
              <tr>
                <th className="py-2 pr-2">Platform</th>
                <th className="py-2 pr-2">Category</th>
                <th className="py-2 pr-2">Commission %</th>
                <th className="py-2 pr-2">Fixed fee / unit (₹)</th>
                <th className="py-2 pr-2">Shipping / unit (₹)</th>
                <th className="py-2 pr-2">Payment %</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {settings.fees.map((fee, index) => {
                const bandCount = (fee.price_bands || []).length + (fee.weight_bands || []).length;
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <React.Fragment key={index}>
                    <tr className="border-t border-gray-100">
                      <td className="py-2 pr-2">
                        <select value={fee.platform} onChange={event => setFee(index, { platform: event.target.value })}
                          className="h-9 rounded-md border border-gray-300 bg-white px-2">
                          {[...new Set([...channelMaster.sales.map(c => c.code), fee.platform].filter(Boolean))].map(code => <option key={code} value={code}>{platformName(code)}</option>)}
                        </select>
                      </td>
                      <td className="py-2 pr-2">
                        <select value={fee.category} onChange={event => setFee(index, { category: event.target.value })}
                          className="h-9 rounded-md border border-gray-300 bg-white px-2">
                          <option value={ALL_CATEGORIES}>All categories</option>
                          {categories.map(category => <option key={category} value={category}>{category}</option>)}
                        </select>
                      </td>
                      {['commission_pct', 'fixed_fee', 'shipping_per_unit', 'payment_pct'].map(field => (
                        <td key={field} className="py-2 pr-2">
                          <Input type="number" min="0" step="0.01" className="w-24" value={fee[field] ?? 0}
                            onChange={event => setFee(index, { [field]: numberField(event.target.value) })} />
                        </td>
                      ))}
                      <td className="whitespace-nowrap py-2 text-right">
                        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setOpenBands(prev => ({ ...prev, [index]: !prev[index] }))}
                          data-testid={`profit-fee-bands-${index}`}>
                          Bands{bandCount ? ` (${bandCount})` : ''}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => removeFee(index)} aria-label="Remove fee rule">
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </td>
                    </tr>
                    {openBands[index] && (
                      <tr>
                        <td colSpan={7} className="bg-gray-50 p-3">
                          <div className="grid gap-4 lg:grid-cols-2">
                            <BandEditor title="Price bands (replace commission % and fixed fee)" limitKey="up_to" limitLabel="Unit price up to (₹)"
                              help="The first band whose limit covers the unit selling price (incl GST) is used. Leave one band without a limit for everything above."
                              bands={fee.price_bands || []} onChange={bands => setFee(index, { price_bands: bands })}
                              fields={[['commission_pct', 'Commission %'], ['fixed_fee', 'Fixed fee (₹)']]} testId={`profit-price-bands-${index}`} />
                            <BandEditor title="Weight bands (replace shipping / unit)" limitKey="up_to_kg" limitLabel="Weight up to (kg)"
                              help="Uses the SKU's weight from SKU Master. SKUs without a weight use the flat shipping above."
                              bands={fee.weight_bands || []} onChange={bands => setFee(index, { weight_bands: bands })}
                              fields={[['fee', 'Shipping (₹)']]} testId={`profit-weight-bands-${index}`} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <Button variant="outline" size="sm" onClick={addFee} data-testid="profit-add-fee">
          <Plus className="mr-2 h-4 w-4" /> Add fee rule
        </Button>
        <div className="flex flex-wrap items-center gap-4 border-t border-gray-100 pt-3 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={settings.fee_gst_claimable !== false} data-testid="profit-fee-gst-claimable"
              onChange={event => update({ fee_gst_claimable: event.target.checked })} />
            GST on marketplace fees is claimed back (input tax credit)
          </label>
          <label className="flex items-center gap-2 text-gray-700">
            GST on fees %
            <Input type="number" min="0" max="100" step="0.01" className="h-8 w-20" value={settings.fee_gst_pct ?? 18}
              onChange={event => update({ fee_gst_pct: numberField(event.target.value) })} />
          </label>
          <span className="text-xs text-gray-500">When not claimed back, this GST is added to the fee cost.</span>
        </div>
      </section>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#007AFF] hover:bg-[#0062cc] text-white" data-testid="profit-save-settings">
          <Save className="mr-2 h-4 w-4" /> {saving ? 'Saving…' : 'Save settings'}
        </Button>
      </div>
      <GstSuggestionsDialog open={gstDialog} onOpenChange={setGstDialog} country={country} onApplied={load} />
    </div>
  );
};

export default function ProfitabilityPage() {
  const { country } = useCountry();
  const [tab, setTab] = useState('report');
  return (
    <div className="space-y-5" data-testid="profitability-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Profitability</h1>
        <p className="mt-1 text-sm text-gray-600">Admin only. Profit after GST, purchase cost and marketplace fees.</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="report" data-testid="profit-tab-report">Report</TabsTrigger>
          <TabsTrigger value="settings" data-testid="profit-tab-settings">Fee &amp; GST settings</TabsTrigger>
        </TabsList>
        <TabsContent value="report" className="mt-4"><ProfitReport country={country} /></TabsContent>
        <TabsContent value="settings" className="mt-4"><ProfitSettings country={country} /></TabsContent>
      </Tabs>
    </div>
  );
}
